import { listCustomFieldDefs, saveContactCustomFieldsBulk } from '../../lib/outreach/api'
import { coerceValue } from '../../lib/outreach/customFields'
import type { CustomFieldDef, CustomFieldValues } from '../../lib/outreach/types'

/**
 * Small surface the legacy CSV import in crm/index.html calls through
 * `window.ShaunIslands.customFields`. Keeps the custom field rules (types,
 * choices, merge semantics) in TypeScript instead of duplicating them in the
 * legacy page.
 */

export interface ImportedCustomRow {
  contactId: string
  /** Raw cell text keyed by custom field key. */
  raw: Record<string, string | null>
}

export async function loadContactDefs(): Promise<CustomFieldDef[]> {
  return listCustomFieldDefs('contact')
}

/**
 * Coerce each imported cell by its field definition and merge the valid
 * values into the contacts' custom fields. Invalid cells are skipped and
 * reported; a cell that is empty never clears an existing value.
 */
export async function saveImported(
  rows: readonly ImportedCustomRow[],
  defs: readonly CustomFieldDef[],
): Promise<{ saved: number; errors: string[] }> {
  const byKey = new Map(defs.map((d) => [d.key, d]))
  const errors = new Set<string>()
  const payload: Array<{ contact_id: string; fields: CustomFieldValues }> = []

  for (const row of rows) {
    const fields: CustomFieldValues = {}
    for (const [key, raw] of Object.entries(row.raw)) {
      const def = byKey.get(key)
      if (!def || raw === null || raw === '') continue
      const res = coerceValue(def, raw)
      if (res.ok) {
        if (res.value !== null) fields[key] = res.value
      } else {
        errors.add(res.error)
      }
    }
    if (Object.keys(fields).length > 0) payload.push({ contact_id: row.contactId, fields })
  }

  if (payload.length > 0) await saveContactCustomFieldsBulk(payload)
  return { saved: payload.length, errors: [...errors].slice(0, 5) }
}
